/**
 * Opening something BAM IQ cited, the way the rest of the platform opens it.
 *
 * Every link is a ResourceRef the server built from the database (UX
 * proposal §E.4); titles never come from the model. Each type opens through
 * the same session stash its own page already reads (the library, X&Os,
 * Resources), so a citation lands on the real item, not a copy of it.
 *
 *   drill / xo / insight   "<section>:<id>"  -> bam_open_drill, then that section
 *   weekly_qa              "<id>"            -> bam_open_qa, then Resources
 *   book                   "doc:<file>"      -> Resources (the books live there)
 *   external / web         an https URL      -> opened outside the app
 *   mention                no link           -> nothing to open
 */
import { openExternal } from "../../utils/nativeBridge";

const SECTIONS = new Set(["pd", "movement", "workouts", "team", "xo", "insights", "ti"]);

const stash = (k, v) => {
  try { sessionStorage.setItem(k, typeof v === "string" ? v : JSON.stringify(v)); } catch { /* private window */ }
};

/** Can this ref be opened? */
export function canOpen(ref) {
  if (!ref) return false;
  switch (ref.type) {
    case "drill": case "xo": case "insight": {
      const [section, id] = String(ref.ref || "").split(":");
      return SECTIONS.has(section) && !!id;
    }
    case "weekly_qa": return !!ref.ref;
    case "book": return true;
    case "external": case "web": return /^https:\/\//.test(String(ref.ref || ""));
    default: return false;
  }
}

/** Open it. `go` is the shell's navigate. Returns what happened. */
export function openRef(ref, go, { open = openExternal } = {}) {
  if (!canOpen(ref)) return null;
  switch (ref.type) {
    case "drill": case "xo": case "insight": {
      const [section, id] = String(ref.ref).split(":");
      stash("bam_open_drill", { sectionId: section, id });
      go(section);
      return "nav";
    }
    case "weekly_qa":
      stash("bam_open_qa", { id: String(ref.ref) });
      go("resources");
      return "nav";
    case "book":
      go("resources");
      return "nav";
    default:
      open(ref.ref);
      return "external";
  }
}

/** How a cited source is labelled for the coach: whose words, in plain terms. */
export function sourceLabel(ref) {
  if (!ref) return "";
  if (ref.type === "web") return "Web";
  if (ref.perspective === "founder") return "Coleman's book";
  if (ref.perspective === "ai_secondary") return "AI summary";
  return "From BAM";
}
